import './CheckrPersonalProfileBanner.scss';

import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Translate from 'components/T';
import styled from 'styled-components';
import { colors } from '@dashboard-experience/mastodon';

import { trackAnalyticsEvent } from 'actions/analytics';
import { CHECKR_PERSONAL_ANALYTICS_EVENTS } from 'lib/analytics';
import useAuthGateState from 'hooks/useAuthGateState';
import useT from 'hooks/useT';
import { PERSONAL_URL } from '../../constants';
import {
  hasBannerViewBeenTracked,
  markBannerViewTracked,
} from './bannerViewTracking';
import { CheckrPersonalUser } from './types';
import { useCheckrPersonalBannerState } from './useCheckrPersonalBannerState';
import { useCheckrPersonalUserQuery } from './useCheckrPersonalUserQuery';

const i18n = {
  dismissBanner: 'components.PersonalChecks.banner.dismiss',
  headline: 'components.PersonalChecks.continueSetupBanner.headline',
  subtext: 'components.PersonalChecks.continueSetupBanner.subtext',
  cta: 'components.PersonalChecks.continueSetupBanner.cta',
  progressLabel: 'components.PersonalChecks.continueSetupBanner.progressLabel',
  chipStatus: {
    completed:
      'components.PersonalChecks.continueSetupBanner.chipStatus.completed',
    pending: 'components.PersonalChecks.continueSetupBanner.chipStatus.pending',
  },
  chips: {
    identity: 'components.PersonalChecks.continueSetupBanner.chips.identity',
    email: 'components.PersonalChecks.continueSetupBanner.chips.email',
    resume: 'components.PersonalChecks.continueSetupBanner.chips.resume',
    basicInfo: 'components.PersonalChecks.continueSetupBanner.chips.basicInfo',
  },
} as const;

const BannerContainer = styled.div.attrs({
  className: 'personal-profile-banner-container',
})`
  background: ${colors.uiGrey0};
  border: 1px solid ${colors.uiGrey200};
  border-radius: 8px;
`;

const Banner = styled.div.attrs({
  className: 'personal-profile-banner',
})`
  padding: 20px;
`;

const DismissButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${colors.uiGrey500};
  cursor: pointer;
  transition: background-color 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
    color 160ms cubic-bezier(0.2, 0.8, 0.2, 1);

  &:hover {
    background: ${colors.uiGrey100};
    color: ${colors.uiGrey700};
  }

  &:focus-visible {
    outline: 2px solid ${colors.uiAqua700};
    outline-offset: 2px;
  }
`;

const CheckIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='15'
    height='15'
    fill='currentColor'
    viewBox='0 0 256 256'
    aria-hidden='true'
  >
    <path d='M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm45.66,85.66-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z'></path>
  </svg>
);

const ClockIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='15'
    height='15'
    fill='currentColor'
    viewBox='0 0 256 256'
    aria-hidden='true'
  >
    <path d='M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm56,112H128a8,8,0,0,1-8-8V72a8,8,0,0,1,16,0v48h48a8,8,0,0,1,0,16Z'></path>
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width='18'
    height='18'
    viewBox='0 0 256 256'
    fill='currentColor'
    aria-hidden='true'
  >
    <path d='M221.66 133.66l-72 72a8 8 0 0 1-11.32-11.32L196.69 136H40a8 8 0 0 1 0-16h156.69l-58.35-58.34a8 8 0 0 1 11.32-11.32l72 72a8 8 0 0 1 0 11.32Z' />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    fill='currentColor'
    viewBox='0 0 256 256'
    aria-hidden='true'
  >
    <path d='M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z'></path>
  </svg>
);

const ProgressRing: React.FC<{
  percent: number;
  ariaLabel: string;
  size?: number;
  stroke?: number;
}> = ({ percent, ariaLabel, size = 52, stroke = 6 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - percent / 100);
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ flexShrink: 0 }}
      role='img'
      aria-label={ariaLabel}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill='none'
        stroke={colors.uiGrey100}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill='none'
        stroke={colors.uiAqua400}
        strokeWidth={stroke}
        strokeLinecap='round'
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x='50%'
        y='50%'
        textAnchor='middle'
        dominantBaseline='central'
        aria-hidden='true'
        style={{
          fontSize: size / 4.2,
          fontWeight: 700,
          fill: colors.uiGrey900,
        }}
      >
        {`${percent}%`}
      </text>
    </svg>
  );
};

type ProfileChip = {
  key: string;
  labelKey: string;
  completed: boolean;
};

const calculateCompletionPercent = (chips: ProfileChip[]) => {
  const totalChips = chips.length;
  const completedChips = chips.filter(chip => chip.completed).length;
  return (completedChips / totalChips) * 100;
};

const useProfileProgress = (user: CheckrPersonalUser | null | undefined) => {
  const identityVerified = !!user?.verified_profile?.identity_verified;
  const hasEmail = !!user?.verified_profile?.email_address;
  const hasResume = !!user?.has_resume;

  const chips = useMemo<ProfileChip[]>(() => {
    const theChips: ProfileChip[] = [
      {
        key: 'identity',
        labelKey: i18n.chips.identity,
        completed: identityVerified,
      },
      {
        key: 'email',
        labelKey: i18n.chips.email,
        completed: hasEmail,
      },
      {
        key: 'resume',
        labelKey: i18n.chips.resume,
        completed: hasResume,
      },
    ];

    if (theChips.every(chip => chip.completed)) {
      theChips.push({
        key: 'basicInfo',
        labelKey: i18n.chips.basicInfo,
        completed: false,
      });
    }

    return theChips;
  }, [identityVerified, hasEmail, hasResume]);

  const completionPercent = useMemo(() => {
    return Math.round(calculateCompletionPercent(chips));
  }, [chips]);

  return { chips, completionPercent };
};

const getLinkUrl = ({ candidateId }: { candidateId?: string }): string => {
  const url = new URL('/api/auth/signup', PERSONAL_URL);
  url.searchParams.set('utm_source', 'candidate_portal');
  url.searchParams.set('utm_medium', 'personal_checks_banner');
  url.searchParams.set('utm_campaign', 'personal_checks_banner');
  url.searchParams.set('onboarding_flow', 'careers');

  if (candidateId) {
    url.searchParams.set('candidate_id', candidateId);
  }

  return url.toString();
};

const useTrackEvent = () => {
  const dispatch = useDispatch();
  const trackEvent = useCallback(
    (eventName: string, eventParams?: Record<string, any>) => {
      dispatch(trackAnalyticsEvent(eventName, eventParams));
    },
    [dispatch],
  );
  return trackEvent;
};

const useTracking = (
  eventProperties: Record<string, unknown>,
  canTrackView: boolean,
) => {
  const trackEvent = useTrackEvent();

  useEffect(() => {
    if (!canTrackView || hasBannerViewBeenTracked()) {
      return;
    }
    trackEvent(
      CHECKR_PERSONAL_ANALYTICS_EVENTS.PROFILES_BANNER_VIEWED,
      eventProperties,
    );
    markBannerViewTracked();
  }, [trackEvent, eventProperties, canTrackView]);

  const trackLinkClick = useCallback(() => {
    trackEvent(
      CHECKR_PERSONAL_ANALYTICS_EVENTS.PROFILES_BANNER_CTA_CLICKED,
      eventProperties,
    );
  }, [trackEvent, eventProperties]);

  const trackDismissClick = useCallback(() => {
    trackEvent(
      CHECKR_PERSONAL_ANALYTICS_EVENTS.PROFILES_BANNER_DISMISSED,
      eventProperties,
    );
  }, [trackEvent, eventProperties]);

  return { trackLinkClick, trackDismissClick };
};

type CheckrPersonalProfileBannerImplProps = {
  onDismiss: () => void;
  accountId: string | undefined;
  candidateId: string | undefined;
  reportId: string | undefined;
};

function CheckrPersonalProfileBannerImpl({
  onDismiss,
  accountId,
  candidateId,
  reportId,
}: CheckrPersonalProfileBannerImplProps) {
  const { t } = useT();
  const {
    data: currentUser,
    isPending: isUserPending,
    isError: isUserError,
  } = useCheckrPersonalUserQuery();
  const { chips, completionPercent } = useProfileProgress(currentUser);

  const {
    candidateLogin: { authMethod },
  } = useAuthGateState();

  const eventProperties = useMemo(() => {
    return {
      account_id: accountId,
      candidate_id: candidateId,
      report_id: reportId,
      location: 'home',
      ...(authMethod ? { auth_method: authMethod } : {}),
    };
  }, [accountId, candidateId, reportId, authMethod]);

  const { trackLinkClick, trackDismissClick } = useTracking(
    eventProperties,
    !isUserPending,
  );

  const linkUrl = useMemo(() => getLinkUrl({ candidateId }), [candidateId]);

  const progressAriaLabel = t(i18n.progressLabel, {
    percent: completionPercent,
  });

  const handleLinkClick = () => {
    trackLinkClick();
  };

  const handleDismiss = () => {
    onDismiss();
    trackDismissClick();
  };

  if (isUserPending) {
    return null;
  }

  return (
    <BannerContainer>
      <DismissButton
        type='button'
        onClick={handleDismiss}
        aria-label={t(i18n.dismissBanner)}
      >
        <CloseIcon />
      </DismissButton>
      <Banner>
        <div className='personal-profile-banner-main'>
          <h3 className='personal-profile-banner-headline'>
            <Translate value={i18n.headline} />
          </h3>
          <div className='personal-profile-banner-body'>
            {!isUserError && (
              <ProgressRing
                percent={completionPercent}
                ariaLabel={progressAriaLabel}
              />
            )}
            <div style={{ minWidth: 0 }}>
              <div className='personal-profile-banner-subtext'>
                <Translate value={i18n.subtext} />
              </div>
              <div className='personal-profile-banner-chips'>
                {chips.map(chip => (
                  <span
                    key={chip.key}
                    data-testid={`personal-profile-banner-chip-${chip.key}`}
                    className={`personal-profile-banner-chip${
                      chip.completed ? ' completed' : ''
                    }`}
                  >
                    <span
                      className={`personal-profile-banner-chip-icon${
                        chip.completed ? ' completed' : ''
                      }`}
                      aria-hidden='true'
                    >
                      {chip.completed ? <CheckIcon /> : <ClockIcon />}
                    </span>
                    <Translate value={chip.labelKey} />
                    <span className='personal-profile-banner-visually-hidden'>
                      {t(
                        chip.completed
                          ? i18n.chipStatus.completed
                          : i18n.chipStatus.pending,
                      )}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className='personal-profile-banner-actions'>
          <a
            href={linkUrl}
            onClick={handleLinkClick}
            className='personal-profile-banner-continue-button'
          >
            <span>
              <Translate value={i18n.cta} />
            </span>
            <ArrowRightIcon />
          </a>
        </div>
      </Banner>
    </BannerContainer>
  );
}

function CheckrPersonalProfileBanner() {
  const { isNewVisible, dismiss, accountId, candidateId, reportId } =
    useCheckrPersonalBannerState();

  if (!isNewVisible) {
    return null;
  }

  return (
    <CheckrPersonalProfileBannerImpl
      onDismiss={dismiss}
      accountId={accountId}
      candidateId={candidateId}
      reportId={reportId}
    />
  );
}

export default CheckrPersonalProfileBanner;
